import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect,
useMemo, useRef } from 'react';
import { useFormContext,useWatch } from 'react-hook-form';
import { z } from 'zod';

import { InventoryItemCondition } from '@ekhodealer/ekho-common';
import {
	Form,
	Stack,
} from '@ekhodealer/ekho-common/rev';

import { useGetInventoryItem } from '../../../api/useGetInventoryItem';
import {
	drawerAtom,
} from '../../../atoms/editDrawerAtoms';
import { FIELD_NAME_TO_LABEL } from '../../../lib/constants';
import { MapPolicy } from '../../../lib/enums';

// Zod schema for pricing form validation
const inventoryPricingSchema = z
		.object({
			dealerRetail: z.coerce
				.number({
					required_error: `${FIELD_NAME_TO_LABEL.dealerRetail} is required`,
					invalid_type_error: `${FIELD_NAME_TO_LABEL.dealerRetail} must be a number`,
				})
				.positive(`${FIELD_NAME_TO_LABEL.dealerRetail} must be greater than $0`),
			advertisedPrice: z.coerce
				.number({
					required_error: `${FIELD_NAME_TO_LABEL.advertisedPrice} is required`,
					invalid_type_error: `${FIELD_NAME_TO_LABEL.advertisedPrice} must be a number`,
				})
				.positive(`${FIELD_NAME_TO_LABEL.advertisedPrice} must be greater than $0`),
			retailPrice: z.coerce
				.number({
					required_error: `${FIELD_NAME_TO_LABEL.retailPrice} is required`,
					invalid_type_error: `${FIELD_NAME_TO_LABEL.retailPrice} must be a number`,
				})
				.positive(`${FIELD_NAME_TO_LABEL.retailPrice} must be greater than $0`),
			isSubjectToMap: z.boolean(),
		})
		.refine(
			(data) => {
				return data.dealerRetail === 0 || data.advertisedPrice === 0 || data.advertisedPrice <= data.dealerRetail;
			},
			{
				message: 'Advertised price must be less than or equal to dealer retail',
				path: ['advertisedPrice'],
			},
		)
		.refine(
			(data) => {
				return data.advertisedPrice === 0 || data.retailPrice === 0 || data.retailPrice <= data.advertisedPrice;
			},
			{
				message: 'Retail price must be less than or equal to advertised price',
				path: ['retailPrice'],
			},
		)
		.refine(
			(data) => {
				return data.dealerRetail === 0 || data.advertisedPrice === 0 || data.dealerRetail >= data.advertisedPrice;
			},
			{
				message: 'Dealer retail must be greater than or equal to advertised price',
				path: ['dealerRetail'],
			},
		)
		.refine(
			(data) => {
				return data.advertisedPrice === 0 || data.retailPrice === 0 || data.advertisedPrice >= data.retailPrice;
			},
			{
				message: 'Advertised price must be greater than or equal to retail price',
				path: ['advertisedPrice'],
			},
		);

// Component that conditionally applies linkedField based on form state
// Must be inside Form to access form context
const ConditionalRetailPriceField = ({
	defaultIsSubjectToMap,
}: {
	defaultIsSubjectToMap: boolean;
}) => {
	const { control } = useFormContext();
	const isSubjectToMapValue = useWatch({
		control,
		name: 'isSubjectToMap',
		defaultValue: defaultIsSubjectToMap,
	});

	return (
		<Form.Field.Text
			schemaField='retailPrice'
			labelText={FIELD_NAME_TO_LABEL.retailPrice}
			descriptionText={
				!isSubjectToMapValue
					? 'Automatically set to match advertised price when not subject to MAP policy. You can still edit it manually if needed.'
					: 'Your true selling price. Hidden until buyers share details. MAP rules still apply.'
			}
			required
			isDisabled={!isSubjectToMapValue}
			linkedField={!isSubjectToMapValue ? 'advertisedPrice' : undefined}
			type='price'
		/>
	);
};

const PricingAtomSync = () => {
	const values = useWatch({
	  name: ['dealerRetail', 'advertisedPrice', 'retailPrice', 'isSubjectToMap'],
	});
  
	const setDrawer = useSetAtom(drawerAtom);
	const isMountedRef = useRef(false);

	useEffect(() => {
	  if (!isMountedRef.current) {
	    isMountedRef.current = true;
	    return;
	  }
	  const [dealerRetail, advertisedPrice, retailPrice, isSubjectToMap] = values;
  
	  // Update drawer draft
	  setDrawer((prev) => ({
		...prev,
		dealerRetail: Number(dealerRetail),
		advertisedPrice: Number(advertisedPrice),
		retailPrice: Number(retailPrice),
		mapPolicy: isSubjectToMap ? MapPolicy.SUBJECT_TO_MAP : MapPolicy.NOT_SUBJECT_TO_MAP,
		hasUserMadeChanges: true,
	  }));
	}, [values, setDrawer]);
  
	return null;
  };

export const PricingSettings = () => {
	const { selectedItemId } = useAtomValue(drawerAtom);
	const { data: inventoryItem } = useGetInventoryItem(selectedItemId || '');

	// Get drawer state values
	const drawerState = useAtomValue(drawerAtom);
	const originalDealerRetail = drawerState.originalDealerRetail || 0;
	const originalAdvertisedPrice = drawerState.originalAdvertisedPrice || 0;
	const originalRetailPrice = drawerState.originalRetailPrice || 0;
	const mapPolicy = drawerState.mapPolicy;

	// Only used vehicles can be subject to MAP
	const isSubjectToMapPolicy =
		inventoryItem?.newUsed === InventoryItemCondition.USED;

	// Convert mapPolicy to boolean for form
	const isSubjectToMap = mapPolicy === MapPolicy.SUBJECT_TO_MAP;

	// Create schema
	const schema = useMemo(() => inventoryPricingSchema, []);

	// Default values from drawer state (convert strings to numbers for schema)
	// When not subject to MAP, retailPrice should match advertisedPrice initially
	const initialRetailPrice =
		mapPolicy === MapPolicy.NOT_SUBJECT_TO_MAP ? originalAdvertisedPrice : originalRetailPrice;

	const defaultValues = {
		dealerRetail: originalDealerRetail,
		advertisedPrice: originalAdvertisedPrice,
		retailPrice: initialRetailPrice,
		isSubjectToMap,
	}

	// Handle form submission (no-op since we sync on change)
	const handleSubmit = async () => {
		return { isSuccess: true };
	};

	return (
		<Form
			schema={schema}
			defaultValues={defaultValues}
			onSubmit={handleSubmit}
			listenForServerChanges={false}>
			<PricingAtomSync />
			<Stack itemsSpacing='16'>
				<Form.Field.Text
					schemaField='dealerRetail'
					labelText={FIELD_NAME_TO_LABEL.dealerRetail}
					descriptionText='You may enter your own retail price here. If provided, it will replace the MSRP as the reference price.'
					required 
					type='price'/>
				<Form.Field.Text
					schemaField='advertisedPrice'
					labelText={FIELD_NAME_TO_LABEL.advertisedPrice}
					descriptionText='The price shown publicly on your listing. If this vehicle is subject to a MAP policy, the advertised price must comply with that rule.'
					required 
					type='price'/>
				<ConditionalRetailPriceField
					defaultIsSubjectToMap={isSubjectToMap}
				/>
				{isSubjectToMapPolicy && (
					<Form.Field.Switch
						schemaField='isSubjectToMap'
						labelText='Subject to MAP policy'
						orientation='horizontal' />
				)}
			</Stack>
		</Form>
	);
};
